import { isMoneyString } from "@ibada/shared-types/finance"
import { ReportValueType } from "@ibada/shared-types/reporting"

import { formatDate, formatDateTime, formatMoney } from "@/features/finance/lib/finance-utils"

/** Whether a value type renders right-aligned (numeric) in tables. */
export function isNumericValueType(valueType: ReportValueType): boolean {
  return (
    valueType === ReportValueType.CURRENCY ||
    valueType === ReportValueType.NUMBER ||
    valueType === ReportValueType.PERCENTAGE
  )
}

/**
 * Format a raw report cell/summary value by its declared value type. Currency uses the active code.
 *
 * A CURRENCY value that arrives as a canonical decimal string is handed to `Intl` UNTOUCHED (IBA-116). It used
 * to go through `Number(value) || 0`, which did two damaging things at once: it dropped an exact amount into a
 * float, and — because `|| 0` treats any falsy result as zero — it rendered an unparseable amount as **$0.00**
 * rather than admitting it could not read it. A report that prints zero where it means "I don't know" is worse
 * than one that prints nothing.
 *
 * A `number` is still formatted, because report sources that have not migrated still send one.
 */
export function formatReportValue(value: unknown, valueType: ReportValueType, currency: string): string {
  if (value == null) return "—"
  switch (valueType) {
    case ReportValueType.CURRENCY:
      if (typeof value === "string") {
        return isMoneyString(value.trim()) ? formatMoney(value.trim(), currency) : "—"
      }
      return typeof value === "number" && Number.isFinite(value) ? formatMoney(value, currency) : "—"
    case ReportValueType.NUMBER:
      return new Intl.NumberFormat("en-US").format(typeof value === "number" ? value : Number(value) || 0)
    case ReportValueType.PERCENTAGE:
      return `${Math.round(typeof value === "number" ? value : Number(value) || 0)}%`
    case ReportValueType.DATE:
      return formatDate(String(value))
    case ReportValueType.DATETIME:
      return formatDateTime(String(value))
    case ReportValueType.BOOLEAN:
      return value ? "Yes" : "No"
    default:
      return String(value)
  }
}
